/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';

import { useAtom } from 'jotai';

import { PathParams } from '../routing/types';
import { $location } from '../state';

const buildPath = <P extends string>(
  route: P,
  ...args: unknown extends PathParams<P> ? [] : [PathParams<P>]
) => {
  const params = args[0];
  return route.replace(/:(\w+)/g, (_, k) => params?.[k as never] ?? _);
};

export const useModuleLocation = () => {
  const [location, navigate] = useAtom($location);

  const setLocation = React.useCallback(
    <P extends string>(
      route: P,
      ...args: unknown extends PathParams<P>
        ?
            | [
                {
                  replace?: boolean;
                  keepSearchParams?: boolean;
                  query?: Record<
                    string,
                    string | number | boolean | null | undefined
                  >;
                  state?: any;
                },
              ]
            | []
        : [
            {
              replace?: boolean;
              keepSearchParams?: boolean;
              query?: Record<
                string,
                string | number | boolean | null | undefined
              >;
              state?: any;
              params: PathParams<P>;
            },
          ]
    ) => {
      const { params: pathParams, query, ...options } = (args[0] ?? {}) as any;
      const newPath = (buildPath as any)(route, pathParams);

      // Handle query parameters
      const searchParams = options.keepSearchParams
        ? new URLSearchParams(location.searchParams)
        : new URLSearchParams();

      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            searchParams.delete(key);
          } else {
            searchParams.set(key, String(value));
          }
        });
      }

      navigate(
        {
          pathname: newPath,
          searchParams,
          ...(location.hash ? { hash: location.hash } : {}),
        },
        {
          replace: !!options.replace,
        },
      );
    },
    [location.hash, location.searchParams, navigate],
  );

  return [location, setLocation] as const;
};
